import { CKEditor, PortalInputField, PortalSelectMenu, PortalSwitchField } from 'components/FormControls';
import { InjectedFormProps, ITranslateProps } from 'models/common';
import { Field } from 'redux-form';
import { IProductForm } from 'models/product/product';
import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { IStore } from 'redux/IStore';
import { PortalPageSection } from 'components/PortalStructure';
import { createDomainValueOptions } from 'helpers/common';
import { RouteComponentProps } from 'react-router';
import { getUserTokenForUpload } from 'redux/modules/magazine/article/managearticle';
import { closeModal, showModal } from 'redux/modules/modal';
import { deleteProduct } from 'redux/modules/product/products';
import { maxLength, required } from 'helpers/validationattributes';
import { BasicButtonGrey } from 'components/Common/Button';
import { ShopStatus } from 'models';
import { EditProductAttributes } from './EditProductAttributes';
import renderIcon from 'helpers/renderIcon';

const maxLength2000 = maxLength(2000);
const maxLength80 = maxLength(80);

interface IParams {
	id: string;
	variantParentId?: string;
}

interface IOwnProps extends RouteComponentProps<IParams, {}> {}

interface IProps
	extends IOwnProps,
		InjectedFormProps<IProductForm>,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps>,
		ITranslateProps {
	currentFormValues: IProductForm;
}

class EditMainProductForm extends React.Component<IProps> {
	constructor(props) {
		super(props);

		this.onFirstCategoryDropdownChange = this.onFirstCategoryDropdownChange.bind(this);
		this.onSecondCategoryDropdownChange = this.onSecondCategoryDropdownChange.bind(this);
		this.openDeleteProduct = this.openDeleteProduct.bind(this);
		this.deleteProduct = this.deleteProduct.bind(this);
	}
	public componentDidMount() {
		this.props.getUserTokenForUpload();
	}

	private get mode(): 'add' | 'edit' {
		if (this.props.params.variantParentId) {
			return 'edit';
		}
		return 'add';
	}

	public onFirstCategoryDropdownChange() {
		this.props.change('secondDropdownCategoryKey', null);
		this.props.change('thirdDropdownCategoryKey', null);
	}

	public onSecondCategoryDropdownChange() {
		this.props.change('thirdDropdownCategoryKey', null);
	}

	private openDeleteProduct() {
		const dialogContent = (
			<div>
				<img src={require('containers/Html/icons/icon_notification.png')} />
				<h2>{this.props.t('BSPortal_Common_ConfirmationDialog_Sure')}</h2>
				<p>{this.props.t('Shop_ManageShop_Products_EditProduct_DeleteProduct')}</p>
				<p>{this.props.t('BSPortal_Common_ConfirmationDialog_Confirm')}</p>
			</div>
		);

		this.props.showModal('ConfirmationDialog', {
			confirmAction: this.deleteProduct,
			yesButtonText: this.props.t('BSPortal_Common_YesDelete'),
			noButtonText: this.props.t('BSPortal_Common_NoWait'),
			content: dialogContent,
			closeModal: this.props.closeModal,
		});
	}

	private deleteProduct(confirm: boolean) {
		const { variantParentId: productId, id } = this.props.params;

		if (confirm) {
			this.props.deleteProduct(productId, id);
		}
		this.props.closeModal();
	}

	public render() {
		const { t, currentFormValues, categories, brands } = this.props;

		let firstDropdownCategories = [];
		let secondDropdownCategories = [];
		let thirdDropdownCategories = [];
		if (categories) {
			firstDropdownCategories = categories.filter((d) => d.parentKey === null);
			if (currentFormValues?.firstDropdownCategoryKey) {
				secondDropdownCategories = categories.filter((x) => x.parentKey === currentFormValues.firstDropdownCategoryKey);
			}
			if (currentFormValues?.secondDropdownCategoryKey) {
				thirdDropdownCategories = categories.filter((x) => x.parentKey === currentFormValues.secondDropdownCategoryKey);
			}
		}

		return (
			<>
				<h1 className="text-center d-none d-lg-block">
					{t(
						this.mode === 'add'
							? 'Shop_ManageShop_Products_CreateProduct_Title'
							: 'Shop_ManageShop_Products_EditProduct_Title',
					)}
				</h1>
				<Field name="id" component="input" type="hidden" />

				<PortalPageSection
					title={t('Shop_ManageShop_Products_CreateProduct_Label_Category')}
					description={t('Shop_ManageShop_Products_CreateProduct_Description_Category')}
				>
					<Field
						name="firstDropdownCategoryKey"
						component={PortalSelectMenu}
						items={createDomainValueOptions(t, 'DV_Categories_', firstDropdownCategories, false)}
						label={t('Shop_ManageShop_Products_CreateProduct_Label_Category')}
						onChangeAction={this.onFirstCategoryDropdownChange}
						isRequired={true}
						validate={[required]}
					/>
					{currentFormValues && currentFormValues.firstDropdownCategoryKey && secondDropdownCategories.length > 0 ? (
						<Field
							name="secondDropdownCategoryKey"
							component={PortalSelectMenu}
							items={createDomainValueOptions(t, 'DV_Categories_', secondDropdownCategories, false)}
							onChangeAction={this.onSecondCategoryDropdownChange}
							isRequired={true}
							validate={[required]}
						/>
					) : null}
					{currentFormValues && currentFormValues.secondDropdownCategoryKey && thirdDropdownCategories.length > 0 ? (
						<Field
							name="thirdDropdownCategoryKey"
							component={PortalSelectMenu}
							items={createDomainValueOptions(t, 'DV_Categories_', thirdDropdownCategories, false)}
						/>
					) : null}
				</PortalPageSection>
				<PortalPageSection title={t('Shop_ManageShop_Products_CreateProduct_Label_Details')}>
					<Field
						name="name"
						component={PortalInputField}
						type="text"
						label={t('Shop_ManageShop_Products_CreateProduct_Label_Title')}
						validate={[required, maxLength80]}
						maxLength={80}
					/>
					<Field
						name="description"
						component={CKEditor}
						validate={[required, maxLength2000]}
						maxLength={2000}
						height={280}
						label={t('Shop_ManageShop_Products_CreateProduct_Label_Description')}
						userPreferences={this.props.userPreferences}
						baseAPI={this.props.baseAPI}
						token={this.props.userTokenForUpload}
					/>
				</PortalPageSection>
				<EditProductAttributes {...this.props} />
				<PortalPageSection
					title={t('Shop_ManageShop_Products_CreateProduct_Title_Brand')}
					description={t('Shop_ManageShop_Products_CreateProduct_Description_Brand')}
				>
					<Field
						name="attributes.brandKey"
						component={PortalSelectMenu}
						items={createDomainValueOptions(t, 'DV_Brands_', brands, false)}
						label={t('Shop_ManageShop_Products_CreateProduct_Title_Brand')}
					/>
					<br />
					<BasicButtonGrey>{t('Shop_ManageShop_Brands_Register_Button')}</BasicButtonGrey>
				</PortalPageSection>
				<PortalPageSection
					title={t('Shop_ManageShop_Products_Label_Status')}
					description={t('BSPortal_Common_Filter_Status_Dropdown_SelectText')}
				>
					<Field
						name="published"
						component={PortalSwitchField}
						type="checkbox"
						isDisabled={currentFormValues.shopStatus === ShopStatus.InApproval}
						label={t('Shop_ManageShop_Products_CreateProduct_Label_ShowProductInShop')}
						tooltipText={
							currentFormValues.shopStatus === ShopStatus.InApproval
								? t('Shop_ManageShop_Products_CreateProduct_Field_Published_TooltipText')
								: null
						}
					/>
					<Field
						name="showInBSPortal"
						component={PortalSwitchField}
						type="checkbox"
						label={t('Shop_ManageShop_Products_CreateProduct_Label_ShowInBSPortal')}
					/>
				</PortalPageSection>
				{this.mode === 'edit' ? (
					<>
						<br />
						<hr />
						<button className="btnText pointer" onClick={this.openDeleteProduct}>
							{renderIcon('delete_2')}
							{t('BSPortal_Common_Delete')}
						</button>
						<br />
					</>
				) : null}
			</>
		);
	}
}

const mapStateToProps = (state: IStore) => ({
	categories: state.domainValues.categories,
	brands: state.domainValues.brands,
	userTokenForUpload: state.manageArticle.userTokenForUpload,
	baseAPI: state.fetchDefault.baseAPI,
	userPreferences: state.userPreferences,
});

const mapDispatchToProps = (dispatch: Dispatch<IStore>) => ({
	getUserTokenForUpload: () => dispatch(getUserTokenForUpload()),
	closeModal: () => dispatch(closeModal()),
	showModal: (modalType: string, modalProps: any) => dispatch(showModal(modalType, modalProps)),
	deleteProduct: (productId: string, shopId: string) => dispatch(deleteProduct(productId, shopId)),
});

const EditMainProduct = connect(mapStateToProps, mapDispatchToProps)(EditMainProductForm);

export { EditMainProduct };
